/**
 * Tax split of one bookable event option or event add-on into several VAT
 * components (e.g. a brunch: food at 7 %, drinks at 19 %). Vendored in
 * gastronaut-api, next-dashboard and skylab — keep the three copies identical.
 *
 * Components only describe the tax split of the option's full effective gross
 * unit price. Ids, quantities, guests and capacity stay on the parent option.
 * Amounts are integer cents; rates are canonical decimal strings ("8.10").
 */

export type VATComponent = {
  id: string;
  title: string;
  grossAmountCents: number;
  vatRate: string;
};

export type VatTaxCountry = 'DE' | 'AT' | 'CH' | 'NL' | 'BE' | 'FR';

export type VatRateKind = 'standard' | 'reduced' | 'special' | 'zero';

export type VatRateEntry = {
  rate: string;
  kind: VatRateKind;
  label: { de: string; en: string };
};

/**
 * Selectable rates per restaurant country. Maintained by hand from primary
 * sources (see spec/CATALOG-SOURCES.md, evidence/catalog-sources.md); this is
 * no product classification. 0 % stays selectable for existing options; its
 * label makes no statement whether a supply is zero-rated or exempt.
 */
export const EVENT_VAT_CATALOG_VERSION = '2026-10-28';

const zeroRate: VatRateEntry = {
  rate: '0.00',
  kind: 'zero',
  label: { de: '0 %', en: '0 %' },
};

export const EVENT_VAT_RATE_CATALOG: Record<VatTaxCountry, VatRateEntry[]> = {
  DE: [
    { rate: '19.00', kind: 'standard', label: { de: '19 %', en: '19 %' } },
    { rate: '7.00', kind: 'reduced', label: { de: '7 %', en: '7 %' } },
    zeroRate,
  ],
  AT: [
    { rate: '20.00', kind: 'standard', label: { de: '20 %', en: '20 %' } },
    { rate: '13.00', kind: 'reduced', label: { de: '13 %', en: '13 %' } },
    { rate: '10.00', kind: 'reduced', label: { de: '10 %', en: '10 %' } },
    { rate: '4.90', kind: 'reduced', label: { de: '4,9 %', en: '4.9 %' } },
    zeroRate,
  ],
  CH: [
    { rate: '8.10', kind: 'standard', label: { de: '8,1 %', en: '8.1 %' } },
    { rate: '3.80', kind: 'special', label: { de: '3,8 % (Beherbergung)', en: '3.8 % (accommodation)' } },
    { rate: '2.60', kind: 'reduced', label: { de: '2,6 %', en: '2.6 %' } },
    zeroRate,
  ],
  NL: [
    { rate: '21.00', kind: 'standard', label: { de: '21 %', en: '21 %' } },
    { rate: '9.00', kind: 'reduced', label: { de: '9 %', en: '9 %' } },
    zeroRate,
  ],
  BE: [
    { rate: '21.00', kind: 'standard', label: { de: '21 %', en: '21 %' } },
    { rate: '12.00', kind: 'reduced', label: { de: '12 %', en: '12 %' } },
    { rate: '6.00', kind: 'reduced', label: { de: '6 %', en: '6 %' } },
    zeroRate,
  ],
  FR: [
    { rate: '20.00', kind: 'standard', label: { de: '20 %', en: '20 %' } },
    { rate: '10.00', kind: 'reduced', label: { de: '10 %', en: '10 %' } },
    { rate: '5.50', kind: 'reduced', label: { de: '5,5 %', en: '5.5 %' } },
    { rate: '2.10', kind: 'reduced', label: { de: '2,1 %', en: '2.1 %' } },
    zeroRate,
  ],
};

export const VAT_TAX_COUNTRY_NAMES: Record<VatTaxCountry, { de: string; en: string }> = {
  DE: { de: 'Deutschland', en: 'Germany' },
  AT: { de: 'Österreich', en: 'Austria' },
  CH: { de: 'Schweiz', en: 'Switzerland' },
  NL: { de: 'Niederlande', en: 'Netherlands' },
  BE: { de: 'Belgien', en: 'Belgium' },
  FR: { de: 'Frankreich (Festland)', en: 'France (mainland)' },
};

const COUNTRY_ALIASES: Record<string, VatTaxCountry> = {
  de: 'DE', deu: 'DE', deutschland: 'DE', germany: 'DE',
  at: 'AT', aut: 'AT', österreich: 'AT', oesterreich: 'AT', austria: 'AT',
  ch: 'CH', che: 'CH', schweiz: 'CH', switzerland: 'CH', suisse: 'CH', svizzera: 'CH',
  nl: 'NL', nld: 'NL', niederlande: 'NL', netherlands: 'NL', nederland: 'NL',
  be: 'BE', bel: 'BE', belgien: 'BE', belgium: 'BE', belgië: 'BE', belgique: 'BE',
  fr: 'FR', fra: 'FR', frankreich: 'FR', france: 'FR',
};

/** Restaurant country from `settings/address.country`; never a silent default. */
export function normalizeVatTaxCountry(value: unknown): VatTaxCountry | null {
  if (typeof value !== 'string') return null;
  const key = value.trim().toLowerCase();
  // Own aliases only: "constructor" or "__proto__" are no countries. ES5-safe.
  return Object.prototype.hasOwnProperty.call(COUNTRY_ALIASES, key) ? COUNTRY_ALIASES[key] : null;
}

/** "8.1", 8.1 or "8,10" → "8.10"; anything else (negative, > 100, > 2 decimals) → null. */
export function canonicalVatRate(value: unknown): string | null {
  const text =
    typeof value === 'number' ? (Number.isFinite(value) ? String(value) : '') : typeof value === 'string' ? value.trim().replace(',', '.') : '';
  if (!/^\d{1,3}(?:\.\d{1,2})?$/.test(text)) return null;
  const hundredths = Math.round(Number(text) * 100);
  if (hundredths > 10000) return null;
  return (hundredths / 100).toFixed(2);
}

/** Rate in hundredths of a percent ("8.10" → 810); exact integer arithmetic. */
export function vatRateBasisPoints(rate: string): number {
  const canonical = canonicalVatRate(rate);
  if (canonical === null) throw new Error(`Invalid VAT rate ${rate}`);
  const [whole, fraction] = canonical.split('.');
  return Number(whole) * 100 + Number(fraction);
}

export function vatRatesForCountry(country: VatTaxCountry | null): VatRateEntry[] {
  return country ? EVENT_VAT_RATE_CATALOG[country] : [];
}

export function isCatalogVatRate(country: VatTaxCountry | null, rate: unknown) {
  const canonical = canonicalVatRate(rate);
  return !!canonical && vatRatesForCountry(country).some((entry) => entry.rate === canonical);
}

export function vatRateLabel(rate: string, language: 'de' | 'en' = 'de') {
  const canonical = canonicalVatRate(rate) ?? rate;
  const text = String(Number(canonical));
  return `${language === 'de' ? text.replace('.', ',') : text} %`;
}

/** VAT contained in a gross amount, rounded half up to the cent. */
export function includedVatCents(grossCents: number, rate: string): number {
  const bp = vatRateBasisPoints(rate);
  if (!bp || !grossCents) return 0;
  const sign = grossCents < 0 ? -1 : 1;
  const numerator = Math.abs(grossCents) * bp;
  const denominator = 10000 + bp;
  return sign * Math.floor((2 * numerator + denominator) / (2 * denominator));
}

/**
 * Splits `totalCents` in proportion to `weights` (largest remainder). Ties for
 * the remaining cents go to the earlier entry, so the result is deterministic
 * and always sums to `totalCents`.
 */
export function splitCentsByWeights(totalCents: number, weights: number[]): number[] {
  if (!Number.isSafeInteger(totalCents)) throw new Error('totalCents must be an integer');
  const sign = totalCents < 0 ? -1 : 1;
  const total = Math.abs(totalCents);
  const positive = weights.map((weight) => (Number.isFinite(weight) && weight > 0 ? weight : 0));
  const weightSum = positive.reduce((sum, weight) => sum + weight, 0);
  if (!weights.length) return [];
  if (weightSum <= 0) return weights.map((_, index) => (index === 0 ? totalCents : 0));
  const exact = positive.map((weight) => (total * weight) / weightSum);
  const shares = exact.map((value) => Math.floor(value + 1e-9));
  let rest = total - shares.reduce((sum, value) => sum + value, 0);
  const order = exact
    .map((value, index) => ({ index, fraction: value - shares[index] }))
    .filter((entry) => positive[entry.index] > 0)
    .sort((a, b) => b.fraction - a.fraction || a.index - b.index);
  for (let i = 0; rest > 0 && order.length; i = (i + 1) % order.length, rest--) {
    shares[order[i].index] += 1;
  }
  return shares.map((share) => sign * share);
}

/** Groups gross lines by canonical rate; VAT is rounded once per rate group. */
export function vatGroups(lines: { grossCents: number; vatRate: string }[]) {
  const byRate = new Map<string, number>();
  for (const line of lines) {
    const rate = canonicalVatRate(line.vatRate);
    if (rate === null) throw new Error(`Invalid VAT rate ${line.vatRate}`);
    byRate.set(rate, (byRate.get(rate) ?? 0) + line.grossCents);
  }
  // Array.from: the guest app compiles for ES5 without downlevelIteration.
  return Array.from(byRate.entries())
    .map(([vatRate, grossCents]) => {
      const vatCents = includedVatCents(grossCents, vatRate);
      return { vatRate, grossCents, vatCents, netCents: grossCents - vatCents };
    })
    .sort((a, b) => vatRateBasisPoints(b.vatRate) - vatRateBasisPoints(a.vatRate));
}

export const toVatCents = (euros: unknown) => Math.round(Number(euros ?? 0) * 100);

type PricedOption = {
  amount?: number | null;
  vatRate?: string;
  vatComponents?: VATComponent[] | null;
};

/** Full gross unit price the components must add up to. */
export function effectiveUnitGrossCents(
  event: { amount?: number | null },
  option: PricedOption,
  kind: 'option' | 'addOn'
) {
  return kind === 'option'
    ? toVatCents((event.amount ?? 0) + (option.amount ?? 0))
    : toVatCents(option.amount ?? 0);
}

/** `undefined`/`null` → legacy single-rate path. Anything else is a split to validate. */
export const hasVatComponents = (option: { vatComponents?: unknown }) =>
  option.vatComponents !== undefined && option.vatComponents !== null;

export type VatComponentIssueCode =
  | 'NOT_A_LIST'
  | 'EMPTY'
  | 'COUNTRY_MISSING'
  | 'RATE_NOT_IN_CATALOG'
  | 'DUPLICATE_ID'
  | 'DUPLICATE_TITLE'
  | 'TITLE_MISSING'
  | 'AMOUNT_INVALID'
  | 'SUM_MISMATCH'
  | 'NO_POSITIVE_COMPONENT'
  | 'NEGATIVE_PRICE'
  | 'DECIMAL_RATE_NEEDS_COMPONENTS'
  | 'DECIMAL_RATE_IN_SPLIT_EVENT';

export type VatComponentIssue = {
  code: VatComponentIssueCode;
  componentIndex?: number;
  differenceCents?: number;
};

const VAT_ISSUE_MESSAGES_DE: Record<VatComponentIssueCode, string> = {
  NOT_A_LIST: 'Die Steueraufteilung ist ungültig.',
  EMPTY: 'Die Steueraufteilung braucht mindestens einen Bestandteil.',
  COUNTRY_MISSING:
    'Für eine Steueraufteilung muss das Land des Restaurants in den Adresseinstellungen hinterlegt sein.',
  RATE_NOT_IN_CATALOG: 'Dieser Steuersatz ist für das Land des Restaurants nicht hinterlegt.',
  DUPLICATE_ID: 'Ein Bestandteil der Steueraufteilung ist doppelt vorhanden.',
  DUPLICATE_TITLE: 'Jeder Bestandteil braucht eine eigene Bezeichnung.',
  TITLE_MISSING: 'Jeder Bestandteil braucht eine Bezeichnung.',
  AMOUNT_INVALID: 'Beträge der Steueraufteilung müssen volle Cent-Beträge ab 0 € sein.',
  SUM_MISMATCH: 'Die Bestandteile müssen zusammen genau den Preis ergeben.',
  NO_POSITIVE_COMPONENT: 'Mindestens ein Bestandteil muss einen Betrag haben.',
  NEGATIVE_PRICE: 'Ein Preis mit Steueraufteilung darf nicht negativ sein.',
  DECIMAL_RATE_NEEDS_COMPONENTS:
    'Dieser Steuersatz wird als Steueraufteilung mit einem Bestandteil gespeichert; bitte im Editor erneut auswählen.',
  DECIMAL_RATE_IN_SPLIT_EVENT:
    'Diese Veranstaltung nutzt Steueraufteilungen. Der Dezimalsatz dieser Option muss deshalb ebenfalls als Steueraufteilung mit einem Bestandteil gespeichert werden; bitte für diese Option die Aufteilung aktivieren und den Satz als einzigen Bestandteil erfassen.',
};

export function vatComponentIssueMessage(issue: VatComponentIssue) {
  return VAT_ISSUE_MESSAGES_DE[issue.code];
}

/**
 * `frozen`: a booked snapshot, validated against the catalog when it was
 * created. Its later use only checks the structure, never the live catalog.
 */
export function validateVatComponents(
  components: unknown,
  effectiveGrossCents: number,
  country: VatTaxCountry | null,
  { frozen = false }: { frozen?: boolean } = {}
): VatComponentIssue[] {
  if (!Array.isArray(components)) return [{ code: 'NOT_A_LIST' }];
  if (!components.length) return [{ code: 'EMPTY' }];
  const issues: VatComponentIssue[] = [];
  if (!country && !frozen) issues.push({ code: 'COUNTRY_MISSING' });
  if (effectiveGrossCents < 0) issues.push({ code: 'NEGATIVE_PRICE' });
  const ids = new Set<string>();
  const titles = new Set<string>();
  let sum = 0;
  let amountsValid = true;
  components.forEach((component: any, componentIndex) => {
    if (!component || typeof component !== 'object') {
      issues.push({ code: 'NOT_A_LIST', componentIndex });
      amountsValid = false;
      return;
    }
    const id = typeof component.id === 'string' ? component.id.trim() : '';
    if (!id || ids.has(id)) issues.push({ code: 'DUPLICATE_ID', componentIndex });
    ids.add(id);
    const title = typeof component.title === 'string' ? component.title.trim() : '';
    if (!title) issues.push({ code: 'TITLE_MISSING', componentIndex });
    else if (titles.has(title.toLowerCase())) issues.push({ code: 'DUPLICATE_TITLE', componentIndex });
    titles.add(title.toLowerCase());
    // Stored rates are canonical strings, so "7", 7 or null is no valid split rate.
    const rate = canonicalVatRate(component.vatRate);
    if (
      rate === null || rate !== component.vatRate
        ? country || frozen
        : country && !frozen && !isCatalogVatRate(country, rate)
    ) {
      issues.push({ code: 'RATE_NOT_IN_CATALOG', componentIndex });
    }
    const cents = component.grossAmountCents;
    if (typeof cents !== 'number' || !Number.isSafeInteger(cents) || cents < 0) {
      issues.push({ code: 'AMOUNT_INVALID', componentIndex });
      amountsValid = false;
    } else {
      sum += cents;
    }
  });
  if (amountsValid && sum !== effectiveGrossCents) {
    issues.push({ code: 'SUM_MISMATCH', differenceCents: effectiveGrossCents - sum });
  }
  if (amountsValid && effectiveGrossCents > 0 && !components.some((c: any) => c.grossAmountCents > 0)) {
    issues.push({ code: 'NO_POSITIVE_COMPONENT' });
  }
  return issues;
}

type EventLike = {
  amount?: number | null;
  options?: (PricedOption & { id: string; internalName?: string })[];
  optionalAddOns?: (PricedOption & { id: string; internalName?: string })[];
};

export type EventVatIssue = VatComponentIssue & {
  kind: 'option' | 'addOn';
  optionId: string;
  optionName?: string;
  /** Problem of the single legacy `vatRate`, not of a split. */
  scalarRate?: boolean;
};

const LEGACY_SCALAR_RATES = new Set(['0.00', '7.00', '19.00', '10.00', '20.00']);

/**
 * A rate the legacy integer tax path cannot represent exactly, e.g. "8.10".
 * New configurations store such a rate as an explicit split (one component);
 * a single `vatRate` never switches an option to the precise path.
 */
export const isPreciseOnlyVatRate = (rate: unknown) => {
  const canonical = canonicalVatRate(rate);
  return canonical !== null && !LEGACY_SCALAR_RATES.has(canonical);
};

/**
 * Every option and add-on that carries a split, or a new non-legacy scalar
 * rate. `previous` is the stored configuration being edited: an unchanged
 * historical decimal `vatRate` without split (grandfathered, legacy path)
 * stays saveable; only a newly chosen one must come as an explicit split.
 * Once any split puts the event on the precise path, its booking snapshot
 * validates every option without `previous`, so no decimal scalar is
 * grandfathered there: it must be an explicit one-component split too.
 */
export function eventVatIssues(
  event: EventLike,
  country: VatTaxCountry | null,
  { previous = null }: { previous?: EventLike | null } = {}
): EventVatIssue[] {
  const issues: EventVatIssue[] = [];
  const precisePath = eventNeedsPreciseVat(event);
  const grandfathered = (kind: 'option' | 'addOn', option: PricedOption & { id: string }) => {
    const before = (kind === 'option' ? previous?.options : previous?.optionalAddOns)?.find(
      (entry) => entry.id === option.id
    );
    return (
      !!before &&
      !hasVatComponents(before) &&
      canonicalVatRate(before.vatRate) !== null &&
      canonicalVatRate(before.vatRate) === canonicalVatRate(option.vatRate)
    );
  };
  const check = (kind: 'option' | 'addOn', option: PricedOption & { id: string; internalName?: string }) => {
    if (hasVatComponents(option)) {
      for (const issue of validateVatComponents(
        option.vatComponents,
        effectiveUnitGrossCents(event, option, kind),
        country
      )) {
        issues.push({ ...issue, kind, optionId: option.id, optionName: option.internalName });
      }
    } else if (isPreciseOnlyVatRate(option.vatRate) && (precisePath || !grandfathered(kind, option))) {
      issues.push({
        code: precisePath ? 'DECIMAL_RATE_IN_SPLIT_EVENT' : 'DECIMAL_RATE_NEEDS_COMPONENTS',
        kind,
        optionId: option.id,
        optionName: option.internalName,
        scalarRate: true,
      });
    }
  };
  (event.options ?? []).forEach((option) => check('option', option));
  (event.optionalAddOns ?? []).forEach((option) => check('addOn', option));
  return issues;
}

/**
 * Booking states that need the precise, versioned tax path: exactly those with
 * an explicit `vatComponents` array on an option or add-on. A missing,
 * `undefined` or `null` field is the original legacy path, whatever `vatRate`.
 */
export function eventNeedsPreciseVat(event: EventLike | null | undefined) {
  return [...(event?.options ?? []), ...(event?.optionalAddOns ?? [])].some(hasVatComponents);
}

/** Percent shares (of the full gross price) → fixed cents; shares must add up to 100. */
export function centsFromPercentShares(totalCents: number, percents: number[]) {
  const hundredths = percents.map((percent) => Math.round(percent * 100));
  if (hundredths.some((value) => !Number.isFinite(value) || value < 0)) return null;
  if (hundredths.reduce((sum, value) => sum + value, 0) !== 10000) return null;
  return splitCentsByWeights(totalCents, hundredths);
}

/** Display only: percent of the full price each stored amount represents. */
export function percentSharesFromCents(totalCents: number, components: { grossAmountCents: number }[]) {
  if (totalCents <= 0) return components.map(() => 0);
  return components.map((component) => Math.round((component.grossAmountCents * 10000) / totalCents) / 100);
}

/** Explicit "Proportional neu verteilen" after a price change. */
export function redistributeVatComponents<T extends { grossAmountCents: number }>(
  components: T[],
  newTotalCents: number
): T[] {
  const weights = components.map((component) => component.grossAmountCents);
  const shares = weights.some((weight) => weight > 0)
    ? splitCentsByWeights(newTotalCents, weights)
    : splitCentsByWeights(newTotalCents, components.map(() => 1));
  return components.map((component, index) => ({ ...component, grossAmountCents: shares[index] }));
}

export const vatComponentsDifferenceCents = (
  components: { grossAmountCents: number }[] | null | undefined,
  totalCents: number
) => totalCents - (components ?? []).reduce((sum, component) => sum + (component.grossAmountCents || 0), 0);

/**
 * Frozen tax context of a booking that uses the precise path. Later changes of
 * the restaurant country or the catalog never touch it.
 */
export type TicketVatSnapshot = {
  version: 1;
  country: VatTaxCountry;
  catalogVersion: string;
  createdAt: number;
};

/** Marker on financial lines (Mollie items) written by the precise path. */
export const VAT_COMPONENTS_CALCULATION = 'vat-components-v1' as const;
